// src/components/patient/AddressSoftCheck.js
//
// Under the patient's address (Contact & Emergency): when the address came from a PFT that
// only one extraction reading read cleanly (a soft check, services/pftSoftChecks.js), say
// so right there, with the PDF and a "Verified" tick, so staff see it without opening the
// PFT section. The tick is the same one as in the PFT date (saved with the page's Save).
// Shows nothing for an address that didn't come from a PFT.

import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { openPftPdf } from "../../services/pftPdfService";
import { softChecksOf, withVerification } from "../../services/pftSoftChecks";

export const addressChecksOf = (tests = [], address = "") =>
  (tests || [])
    .filter((t) => t && !t.removedAt)
    .map((t) => ({ test: t, check: softChecksOf(t, { patientAddress: address }).find((c) => c.key === "address") }))
    .filter((x) => x.check);

export default function AddressSoftCheck({ tests, address, userEmail, onChange, onError }) {
  const [opening, setOpening] = useState("");
  const list = addressChecksOf(tests, address);
  if (!list.length) return null;
  const open = async (t) => {
    setOpening(t.id);
    try {
      await openPftPdf(t.file);
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setOpening("");
    }
  };
  return (
    <div className="space-y-1.5">
      {list.map(({ test: t, check: c }) => (
        <div key={t.id} className="pft-soft-check rounded-lg border border-amber-400/30 bg-amber-500/[0.06] px-3 py-2 text-[12px]">
          {c.verified ? (
            <span className="pft-soft-verified text-emerald-300">
              Address from the PFT of {t.date || "an undated PFT"}: {c.decidedInMerge ? "chosen in a merge" : "verified"}
              {c.verifiedBy ? ` by ${c.verifiedBy}` : ""}
              {c.verifiedAt ? ` on ${String(c.verifiedAt).slice(0, 10)}` : ""}
            </span>
          ) : (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-amber-200 font-medium">
                Check this address against the PDF (PFT {t.date || "with no date"}).
              </span>
              <span className="text-[11px] text-slate-400">{c.reason}</span>
              <span className="ml-auto flex items-center gap-2">
                {t.file && (
                  <button
                    type="button"
                    onClick={() => open(t)}
                    disabled={opening === t.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] text-slate-200 transition disabled:opacity-40"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {opening === t.id ? "Opening…" : "Open PDF"}
                  </button>
                )}
                <label className="inline-flex items-center gap-1 text-[11px] text-slate-200">
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={(e) =>
                      onChange(
                        tests.map((x) =>
                          x.id === t.id ? withVerification(x, "address", { checked: e.target.checked, by: userEmail, now: new Date().toISOString() }) : x,
                        ),
                      )
                    }
                    className="accent-emerald-500"
                    aria-label="Address verified against the PDF"
                  />
                  Verified
                </label>
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
